import { SourceFilters, Provenance } from '../features/agency/ui';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useRewardList,
  REWARD_STATUS_LABELS,
  REWARD_RANGE_LABELS,
  ALL_REWARD_RANGES,
} from '../features/rewards';
import type { RewardDTO, RewardRange, RewardStatus } from '../features/rewards';
import {
  useNewcomerRewardList,
  NEWCOMER_REWARD_RANGE_LABELS,
  NEWCOMER_REWARD_STATUS_LABELS,
  ALL_NEWCOMER_REWARD_RANGES,
  ALL_NEWCOMER_REWARD_STATUSES,
} from '../features/newcomer-rewards';
import type {
  NewcomerRewardDTO,
  NewcomerRewardRange,
  NewcomerRewardStatus,
} from '../features/newcomer-rewards';

type RewardTab = 'task' | 'newcomer';

const STATUS_STYLES: Record<RewardStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  settled: 'bg-green-50 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const NEWCOMER_STATUS_STYLES: Record<NewcomerRewardStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  settled: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function ConditionText({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={ok ? 'text-green-700' : 'text-gray-400'}>
      {label}{ok ? '已满足' : '未满足'}
    </span>
  );
}

function Pagination({
  total,
  page,
  totalPages,
  setPage,
}: {
  total: number;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
      <span>共 {total} 条</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page <= 1}
          className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
        >
          上一页
        </button>
        <span className="text-sm tabular-nums">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages}
          className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
        >
          下一页
        </button>
      </div>
    </div>
  );
}

function TaskRewardRows({ items }: { items: RewardDTO[] }) {
  return (
    <tbody>
      {items.map((item) => (
        <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
          <td className="px-4 py-3 text-gray-500 tabular-nums text-xs">{item.id}</td>
          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
            <span>{item.user_nickname || '-'}</span>
            <span className="text-gray-400 text-xs ml-1">#{item.user_id}</span>
          </td>
          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{item.task_title}<Provenance item={item}/></td>
          <td className="px-4 py-3 text-center text-gray-500 tabular-nums text-xs">{item.assignment_id}</td>
          <td className="px-4 py-3 text-right tabular-nums text-gray-900">&yen;{item.amount_yuan}</td>
          <td className="px-4 py-3 text-center">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[item.status as RewardStatus] ?? 'bg-gray-100 text-gray-600'}`}>
              {REWARD_STATUS_LABELS[item.status as RewardStatus] ?? item.status}
            </span>
          </td>
          <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">{formatDateTime(item.created_at)}</td>
          <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">{formatDateTime(item.settled_at)}</td>
        </tr>
      ))}
    </tbody>
  );
}

function NewcomerRewardRows({ items }: { items: NewcomerRewardDTO[] }) {
  return (
    <tbody>
      {items.map((item) => (
        <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
          <td className="px-4 py-3 text-gray-500 tabular-nums text-xs">{item.id}</td>
          <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
            <span>{item.user_nickname || '-'}</span>
            <span className="text-gray-400 text-xs ml-1">#{item.user_id}</span>
          </td>
          <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDateTime(item.registered_at)}</td>
          <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">
            <div className="flex flex-col gap-1">
              <ConditionText ok={item.withdrawal_account_bound ?? item.alipay_bound} label={item.market_id === 'VN' ? '银行卡' : '支付宝'} />
              <ConditionText ok={item.apple_verified} label="Apple " />
              <ConditionText ok={item.first_task_approved} label="首单" />
            </div>
          </td>
          <td className="px-4 py-3 text-center text-gray-500 tabular-nums text-xs">
            {item.first_approved_assignment_id ?? '-'}
          </td>
          <td className="px-4 py-3 text-right tabular-nums text-gray-900">{item.amount_display ?? `¥${item.amount_yuan}`}</td>
          <td className="px-4 py-3 text-center">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${NEWCOMER_STATUS_STYLES[item.status as NewcomerRewardStatus] ?? 'bg-gray-100 text-gray-600'}`}>
              {NEWCOMER_REWARD_STATUS_LABELS[item.status as NewcomerRewardStatus] ?? item.status}
            </span>
          </td>
          <td className="px-4 py-3 text-center text-gray-500 tabular-nums text-xs">{item.attempt_count}</td>
          <td className="px-4 py-3 text-gray-500 text-xs max-w-[220px] truncate" title={item.failure_reason || ''}>
            {item.failure_reason || '-'}
          </td>
          <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">{formatDateTime(item.settled_at)}</td>
        </tr>
      ))}
    </tbody>
  );
}

export function RewardsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: RewardTab = searchParams.get('rewardTab') === 'newcomer' ? 'newcomer' : 'task';
  const taskRewards = useRewardList(activeTab === 'task');
  const newcomerRewards = useNewcomerRewardList(activeTab === 'newcomer');

  const setActiveTab = useCallback((tab: RewardTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'newcomer') next.set('rewardTab', 'newcomer');
      else next.delete('rewardTab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="space-y-4">
      {activeTab==='task'&&<SourceFilters />}
      <p className="text-sm text-gray-500">
        查看任务奖励与新人注册奖励记录，两类奖励使用独立数据源和筛选条件。
      </p>

      <div className="flex items-center gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('task')}
          className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'task' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          任务奖励
        </button>
        <button
          onClick={() => setActiveTab('newcomer')}
          className={`pb-2 text-sm font-medium transition-colors ${activeTab === 'newcomer' ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
        >
          新人奖励
        </button>
      </div>

      {activeTab === 'task' ? (
        <section className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={taskRewards.userId}
              onChange={(e) => taskRewards.setUserId(e.target.value.replace(/\D/g, ''))}
              placeholder="用户 ID"
              className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={taskRewards.range}
              onChange={(e) => taskRewards.setRange(e.target.value)}
              className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {ALL_REWARD_RANGES.map((value) => (
                <option key={value || 'all'} value={value}>
                  {REWARD_RANGE_LABELS[value as RewardRange]}
                </option>
              ))}
            </select>
            <button onClick={taskRewards.resetFilters} className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">重置</button>
            <button onClick={taskRewards.reload} className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">刷新</button>
          </div>

          {taskRewards.error && (
            <div className="py-10 text-center">
              <p className="text-sm text-red-500 mb-3">{taskRewards.error}</p>
              <button onClick={taskRewards.reload} className="text-sm text-primary hover:underline">重试</button>
            </div>
          )}

          {taskRewards.loading && !taskRewards.error && (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {!taskRewards.loading && !taskRewards.error && (
            <>
              {taskRewards.items.length === 0 ? (
                <div className="py-20 text-center text-sm text-gray-400">当前暂无任务奖励记录，或已被筛选条件过滤。</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">任务标题</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">任务分配ID</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">金额(元)</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">结算时间</th>
                      </tr>
                    </thead>
                    <TaskRewardRows items={taskRewards.items} />
                  </table>
                </div>
              )}
              <Pagination total={taskRewards.total} page={taskRewards.page} totalPages={taskRewards.totalPages} setPage={taskRewards.setPage} />
            </>
          )}
        </section>
      ) : (
        <section className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="text"
              value={newcomerRewards.userId}
              onChange={(e) => newcomerRewards.setUserId(e.target.value.replace(/\D/g, ''))}
              placeholder="用户 ID"
              className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={newcomerRewards.status}
              onChange={(e) => newcomerRewards.setStatus(e.target.value)}
              className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {ALL_NEWCOMER_REWARD_STATUSES.map((value) => (
                <option key={value || 'all'} value={value}>
                  {value ? NEWCOMER_REWARD_STATUS_LABELS[value as NewcomerRewardStatus] : '全部状态'}
                </option>
              ))}
            </select>
            <select
              value={newcomerRewards.range}
              onChange={(e) => newcomerRewards.setRange(e.target.value)}
              className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {ALL_NEWCOMER_REWARD_RANGES.map((value) => (
                <option key={value || 'all'} value={value}>
                  {NEWCOMER_REWARD_RANGE_LABELS[value as NewcomerRewardRange]}
                </option>
              ))}
            </select>
            <button onClick={newcomerRewards.resetFilters} className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">重置</button>
            <button onClick={newcomerRewards.reload} className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">刷新</button>
          </div>

          {newcomerRewards.error && (
            <div className="py-10 text-center">
              <p className="text-sm text-red-500 mb-3">{newcomerRewards.error}</p>
              <button onClick={newcomerRewards.reload} className="text-sm text-primary hover:underline">重试</button>
            </div>
          )}

          {newcomerRewards.loading && !newcomerRewards.error && (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {!newcomerRewards.loading && !newcomerRewards.error && (
            <>
              {newcomerRewards.items.length === 0 ? (
                <div className="py-20 text-center text-sm text-gray-400">当前暂无新人奖励记录，或已被筛选条件过滤。</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">注册时间</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">条件</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">首单分配ID</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">金额(元)</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">尝试</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">失败原因</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">发放时间</th>
                      </tr>
                    </thead>
                    <NewcomerRewardRows items={newcomerRewards.items} />
                  </table>
                </div>
              )}
              <Pagination total={newcomerRewards.total} page={newcomerRewards.page} totalPages={newcomerRewards.totalPages} setPage={newcomerRewards.setPage} />
            </>
          )}
        </section>
      )}
    </div>
  );
}
